import React, { useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { HeroChat } from './HeroChat';
import { useChatConversation } from '../hooks/useChatConversation';
import { CHAT_AGAIN, CHAT_START } from '../data/chatScript';
import type { BubbleLink, ChatPhase } from '../types/chat';
import type { Emotion } from '../types/emotion';
interface RobotChatProps {
  reduce: boolean;
  /** The section spot — the chat is rendered inside it, next to Pixel */
  hostRef: RefObject<HTMLElement>;
  /** Pixel is in his section spot and has finished entering it */
  active: boolean;
  /** Wait this long after becoming active before the first conversation (ms) */
  startDelay: number;
  /** Drone has left (or is leaving) for the bottom-right corner — no chat there */
  docked: boolean;
  /** Full-charge easter egg is running — bubbles step aside */
  busy: boolean;
  /** Hover link shared with the drone (holds him still over the bubbles) */
  bubbleLink: BubbleLink;
  onPhase: (phase: ChatPhase, emotion: Emotion) => void;
  onPulse: () => void;
  /** Conversation this spot opens with (default: the hero intro) */
  startNode?: string;
  /** Conversation he restarts with after getting bored */
  againNode?: string;
}

/** After the last line of a finished conversation, the notes pop away */
const ENDED_HIDE_AFTER = 2600;
/** Gap between the notes popping away and Pixel being free to fly off (ms) */
const RELEASE_AFTER_HIDE = 350;
/** After a goodbye, Pixel gets bored and starts again */
const RESTART_AFTER = 22000;

/** Pixel's chat — only in his section spot. His notes pop up at his upper-right while he holds his spot. */
export function RobotChat({
  reduce,
  hostRef,
  active,
  startDelay,
  docked,
  busy,
  bubbleLink,
  onPhase,
  onPulse,
  startNode = CHAT_START,
  againNode = CHAT_AGAIN
}: RobotChatProps) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [endedHidden, setEndedHidden] = useState(false);
  useEffect(() => {
    setHost(hostRef.current);
  }, [hostRef]);
  const live = active && !docked;
  const showBubbles = live && !busy && !endedHidden && !!host;
  const visibleRef = useRef(false);
  visibleRef.current = showBubbles;
  const chat = useChatConversation({
    reduce,
    visibleRef,
    onPhase,
    onPulse
  });
  const {
    play,
    pause,
    resume,
    ended
  } = chat;

  // Bubbles unmount without a pointerleave — release the drone's "hold still"
  useEffect(() => {
    if (!showBubbles) bubbleLink.hovered = false;
  }, [showBubbles, bubbleLink]);

  // Out of the spot (back in the hero, or docked): everything pops away and he goes quiet.
  // Settled in the spot: first time he starts after startDelay; later he picks the thread back up.
  useEffect(() => {
    if (!live) {
      pause();
      return;
    }
    if (!resume()) play(startNode, startDelay);
  }, [live, pause, resume, play, startDelay, startNode]);

  // Conversation over: notes pop away, Pixel roams, then gets bored and starts again
  useEffect(() => {
    if (!ended) {
      setEndedHidden(false);
      return;
    }
    if (!live) return;
    const hide = window.setTimeout(() => setEndedHidden(true), ENDED_HIDE_AFTER);
    // Release him to roam only after the notes have finished popping away
    const release = window.setTimeout(() => onPhase('idle', 'neutral'), ENDED_HIDE_AFTER + RELEASE_AFTER_HIDE);
    const again = window.setTimeout(() => play(againNode), RESTART_AFTER);
    return () => {
      window.clearTimeout(hide);
      window.clearTimeout(release);
      window.clearTimeout(again);
    };
  }, [ended, live, play, onPhase, againNode]);
  if (!host) return null;
  return createPortal(<HeroChat show={showBubbles} reduce={reduce} bubbleLink={bubbleLink} messages={chat.messages} burst={chat.burst} thinking={chat.thinking} options={chat.options} ready={chat.ready} picked={chat.picked} onChoose={chat.choose} onClose={chat.dismiss} data-id="element-264" data-mp="oqi6tlswfr0n" />, host);
}